const SAVED_VIEWS_KEY = "firestoreAdmin.savedViews";

function loadAll() {
  try {
    const raw = localStorage.getItem(SAVED_VIEWS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistAll(list) {
  try {
    localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota errors */
  }
}

function makeId() {
  return `view_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function listViews() {
  return loadAll().sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return (b.createdAt || "").localeCompare(a.createdAt || "");
  });
}

export function saveView(view) {
  const list = loadAll();
  const record = {
    id: makeId(),
    name: view.name || "Untitled view",
    collection: view.collection || "",
    docIdQuery: view.docIdQuery || "",
    fieldQuery: view.fieldQuery || "",
    sortField: view.sortField || "id",
    sortAsc: view.sortAsc !== false,
    recordLimit: view.recordLimit || 100,
    pinned: false,
    createdAt: new Date().toISOString(),
  };
  list.push(record);
  persistAll(list);
  return record;
}

export function updateView(id, patch) {
  const list = loadAll().map((v) => (v.id === id ? { ...v, ...patch } : v));
  persistAll(list);
}

export function deleteView(id) {
  persistAll(loadAll().filter((v) => v.id !== id));
}

export function togglePinned(id) {
  const list = loadAll().map((v) => (v.id === id ? { ...v, pinned: !v.pinned } : v));
  persistAll(list);
}

/* ------------------------------------------------------------------ */
/* Sprint 11 — Enterprise Explorer views                               */
/* Additive: richer views capturing the full Collection Explorer state */
/* (collection, columns, filters, sort, density, page size, search).   */
/* Stored under a separate key so the legacy views above keep working.  */
/* ------------------------------------------------------------------ */

const EXPLORER_VIEWS_KEY = "firestoreAdmin.explorer.views.v1";

function loadExplorerViews() {
  try {
    const raw = localStorage.getItem(EXPLORER_VIEWS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistExplorerViews(list) {
  try {
    localStorage.setItem(EXPLORER_VIEWS_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota errors */
  }
}

export function listExplorerViews(collectionName = null) {
  const all = loadExplorerViews();
  const scoped = collectionName ? all.filter((v) => !v.collection || v.collection === collectionName) : all;
  return scoped.sort((a, b) => {
    if (Boolean(b.pinned) !== Boolean(a.pinned)) return b.pinned ? 1 : -1;
    return (b.createdAt || "").localeCompare(a.createdAt || "");
  });
}

export function saveExplorerView(view) {
  const list = loadExplorerViews();
  const record = {
    id: makeId(),
    name: view.name || "Untitled view",
    collection: view.collection || "",
    state: {
      globalSearch: view.state?.globalSearch || "",
      filters: Array.isArray(view.state?.filters) ? view.state.filters : [],
      attendance: view.state?.attendance || null,
      columnPrefs: view.state?.columnPrefs || null,
      sortField: view.state?.sortField || "id",
      sortAsc: view.state?.sortAsc !== false,
      pageSize: view.state?.pageSize || 100,
      density: view.state?.density || "standard",
    },
    pinned: false,
    createdAt: new Date().toISOString(),
  };
  list.push(record);
  persistExplorerViews(list);
  return record;
}

export function deleteExplorerView(id) {
  persistExplorerViews(loadExplorerViews().filter((v) => v.id !== id));
}

export function toggleExplorerViewPinned(id) {
  persistExplorerViews(loadExplorerViews().map((v) => (v.id === id ? { ...v, pinned: !v.pinned } : v)));
}

export function clearAllSavedViews() {
  persistAll([]);
  try {
    localStorage.removeItem("firestoreAdmin.explorer.views.v1");
  } catch {
    /* ignore */
  }
}
